import { CircularProgress, Stack, TextField } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { useRoles } from 'api/attributes';
import { useCurrentGuideIdPID } from 'contexts/IdPConfigContext';
import {
	useCurrentGuideMethod,
	useSSOConfigMappings
} from 'contexts/SSOConfigFieldsContext';
import React, { useMemo } from 'react';
import {
	RoleOption,
	filterRoleOptions,
	renderRoleOption,
	toRoleOptions
} from './roleOptions';

const DefaultSSORolesField: React.FC = () => {
	const idpID = useCurrentGuideIdPID();
	const method = useCurrentGuideMethod();

	const { getDefaultSSORoles, setDefaultSSORoles } = useSSOConfigMappings();

	const {
		isLoading: isRolesLoading,
		isIdle: isRolesIdle,
		data: rolesData
	} = useRoles();

	const rolesOptions = useMemo<RoleOption[]>(
		() => toRoleOptions(rolesData),
		[rolesData]
	);

	const stored = useMemo<string[]>(
		() => (idpID && method && getDefaultSSORoles()) || [],
		[idpID, method, getDefaultSSORoles]
	);

	const value = useMemo<RoleOption[]>(
		() =>
			stored.map(
				(id) =>
					rolesOptions.find((o) => o.value === id) ?? { value: id, label: id }
			),
		[rolesOptions, stored]
	);

	if (isRolesLoading || isRolesIdle) {
		return (
			<Stack justifyContent="center" alignItems="center" height={200}>
				<CircularProgress />
			</Stack>
		);
	}

	return (
		<Autocomplete<RoleOption, true>
			data-testid="default-sso-roles-input"
			size="small"
			multiple
			options={rolesOptions}
			value={value}
			getOptionLabel={(opt) => opt.label}
			isOptionEqualToValue={(opt, val) => opt.value === val.value}
			filterOptions={filterRoleOptions}
			renderOption={renderRoleOption}
			onChange={(_, newValue) => {
				setDefaultSSORoles(newValue.map((o) => o.value));
			}}
			sx={{ flexGrow: 1, flexBasis: 0 }}
			renderInput={(params) => (
				<TextField
					{...params}
					data-testid="default-sso-roles-field"
					label={intl.tr('ssoConfig.fields.defaultSSORoles')}
					slotProps={{
						inputLabel: { shrink: true }
					}}
				/>
			)}
		/>
	);
};

export default DefaultSSORolesField;
